<template>
  <div id="main" class="main theme-mini" :class="{ mobile: isMobileOnly }">
    <div id="nav">
      <router-link to="/home" v-slot="{ navigate }">
        <div id="logo" @click="navigate">
          <img src="../assets/et.png" alt="" />
        </div>
      </router-link>
      <div id="right-nav">
        <!-- NOTIFICATION BELL -->
        <div class="relative inline-flex items-center" v-if="!isMobileOnly">
          <button
            type="button"
            class="relative inline-flex h-8 w-8 items-center justify-center rounded-sm top-1 right-3 border border-transparent text-slate-400 transition-colors focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#13191d]"
            :class="showNotifications ? 'bg-white/5 text-slate-100' : 'hover:bg-white/5 hover:text-slate-100'"
            :aria-expanded="showNotifications ? 'true' : 'false'"
            aria-label="Notifications"
            title="Notifications"
            @click="showNotifications = !showNotifications"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-[18px] w-[18px]"
              :class="unreadCount > 0 && !showNotifications ? 'text-slate-200' : ''"
            >
              <g>
                <path d="M14.24 21H9.76a0.25 0.25 0 0 0 -0.24 0.22 2.64 2.64 0 0 0 0 0.28 2.5 2.5 0 0 0 5 0 2.64 2.64 0 0 0 0 -0.28 0.25 0.25 0 0 0 -0.28 -0.22Z" fill="currentColor" stroke-width="1"></path>
                <path d="M21.45 19.28a16.29 16.29 0 0 1 -1.75 -7.34v-0.76a9.11 9.11 0 0 0 -3.87 -7.63 6.31 6.31 0 0 0 -2.33 -0.91V1.5a1.5 1.5 0 0 0 -3 0v1.15a6.68 6.68 0 0 0 -2.85 1.28 9.18 9.18 0 0 0 -3.35 7.25v0.76a16.29 16.29 0 0 1 -1.75 7.34 0.51 0.51 0 0 0 0 0.48A0.52 0.52 0 0 0 3 20h18a0.52 0.52 0 0 0 0.43 -0.24 0.51 0.51 0 0 0 0.02 -0.48Z" fill="currentColor" stroke-width="1"></path>
              </g>
            </svg>
            <span
              v-if="unreadCount > 0"
              class="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full border border-[#13191d] bg-red-500 px-1 text-[10px] font-bold leading-4 text-white"
            >
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </button>
          <div
            v-if="showNotifications"
            class="absolute right-0 top-8 z-50 w-[22rem] overflow-hidden rounded-md border border-white/10 bg-[#13191d] shadow-xl ring-1 ring-black/40"
          >
            <div class="flex items-center justify-between border-b border-white/10 px-4 pt-3">
              <div class="pr-3">
                <span class="block text-sm font-semibold text-slate-100">Notifications</span>
                <span class="block text-[11px] text-slate-500">{{ allNotifications.length ? `${allNotifications.length} total` : 'No new activity' }}</span>
              </div>
              <button
                v-if="allNotifications.length"
                type="button"
                class="text-xs font-medium text-slate-400 transition-colors hover:text-slate-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#13191d]"
                @click="markAllAsRead"
              >
                Mark all read
              </button>
            </div>
            <div class="max-h-[26rem] overflow-y-auto divide-y divide-white/5">
              <div v-if="!allNotifications.length" class="px-4 py-10 text-center text-sm text-slate-500">No notifications</div>
              <div
                v-for="(n, i) in allNotifications"
                :key="i"
                class="relative px-4 pb-3 transition-colors hover:bg-white/5 cursor-pointer"
                :class="n.read ? 'bg-transparent' : 'bg-red-500/5'"
                @click="markRead(i)"
              >
                <!-- TOP HEADER: STATUS DOT + STATUS + TIME -->
                <div class="flex items-center justify-between mb-1">
                  <div class="flex items-center gap-1.5">
                    <div
                      class="h-2 w-2 rounded-full"
                      :class="statusDotClass(n.status)"
                    ></div>
                    <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {{ n.status || 'INFO' }}
                    </span>
                  </div>
                  <span class="text-[10px] tabular-nums text-slate-500">{{ formatTime(n.timestamp) }}</span>
                </div>
                <!-- TYPE + NAME -->
                <div class="text-[10px] pb-1 font-medium tracking-tight text-slate-500 uppercase leading-none">
                  {{ n.type || 'System Notification' }}
                </div>
                <div class="text-sm font-bold text-slate-100 leading-tight mt-0.5">
                  {{ n.inputName }}
                </div>
                <!-- ERROR MESSAGE -->
                <div v-if="n.data && n.data.errorMessage" class="mt-1.5 rounded-sm bg-black/40 p-2 text-[11px] font-mono leading-relaxed text-red-400/90 border border-red-500/10">
                  {{ n.data.errorMessage }}
                </div>

                <!-- UNREAD INDICATOR BAR -->
                <div v-if="!n.read" class="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500"></div>
              </div>
            </div>
          </div>
          <div v-if="showNotifications" class="fixed inset-0 z-40" @click="showNotifications = false"></div>
        </div>
        <!-- MOBILE PUSH MENU -->
        <span class="mobile-toggle" v-if="isMobileOnly" @click="mobileNav = true">
          <el-avatar
            size="small"
            shape="square"
            :style="{ background: avatarBackgroundColor, color: avatarTextColor }"
          >
            {{ initials }}
          </el-avatar>
          <i class="el-icon-arrow-down el-icon--right"></i>
        </span>
        <el-drawer
          custom-class="main-push-nav"
          size="300px"
          :title="userName"
          :visible.sync="mobileNav"
          :with-header="false"
        >
          <div v-bar class="drawer-wrapper">
            <div>
              <el-dropdown-item disabled divided> Account(s) </el-dropdown-item>
              <el-dropdown-item
                @click.native="setClientAccount({ item, accountId: item.account.id })"
                v-for="item in avaiableAccounts"
                :key="item.account.id + 'nav'"
                :disabled="item.account.id === account.id"
              >
                <div class="dd-account-name">
                  {{ item.account.name }}
                  <el-tag
                    v-show="item.account.id === account.id"
                    size="mini"
                    class="muted"
                    type="info"
                    >active</el-tag
                  >
                </div>
                <small class="dd-account-client">{{ item.client.name }}</small>
              </el-dropdown-item>
              <el-dropdown-item icon="bi bly-user" @click.native="profile" divided>
                Edit profile
              </el-dropdown-item>
              <el-dropdown-item icon="bi bly-logout" @click.native="logout">
                Logout
              </el-dropdown-item>
            </div>
          </div>
        </el-drawer>
        <!-- DESKTOP MENU -->
        <el-popover placement="bottom" trigger="manual" v-model="showUserMenu">
          <!-- ACCOUNT LIST DESKTOP -->
          <div class="flex items-center justify-between">
            <div>
              <div class="font-bold text-black" @click="profile">{{ userName }}</div>
              <div
                class="nav-profile-account my-0 mt-1 text-xs text-blue-500 cursor-pointer"
                @click="openTermsOfService"
              >
                Terms of Service
              </div>
            </div>
            <div class="user-actions">
              <el-tooltip content="User settings">
                <i class="bi bly-edit icon-btn" @click="profile"></i>
              </el-tooltip>
              <el-tooltip content="Logout">
                <i class="bi bly-logout icon-btn" @click="logout"></i>
              </el-tooltip>
            </div>
          </div>
          <div v-bar class="dd-popup-wrapper">
            <div>
              <div v-for="clientItem in avaiableClients" :key="clientItem.client.id">
                <el-divider content-position="left">{{ clientItem.client.name }}</el-divider>
                <div class="dd-client-account-list">
                  <div
                    class="dd-account-item"
                    :class="{ active: item.account.id === account.id }"
                    @click="
                      setClientAccount({ item, accountId: item.account.id });
                      mobileNav = false;
                    "
                    v-for="item in clientItem.accounts"
                    :key="item.account.id + 'nav'"
                    :disabled="item.account.id === account.id"
                  >
                    <div class="dd-account-name">
                      {{ item.account.name }}
                      <!-- <br>
                      <small>{{item.client.name}}</small>
                      -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- END ACCOUNT LIST DESKTOP -->
          <span slot="reference" v-if="!isMobileOnly" class="el-dropdown-link" @click="showUserMenu = !showUserMenu">
            <div class="nav-profile hidden md:visible">
              <div class="nav-profile-name">{{ userName }}</div>
              <div class="nav-profile-account">{{ accountName }}</div>
            </div>
            <el-avatar
              size="small"
              shape="square"
              :style="{ background: avatarBackgroundColor, color: avatarTextColor }"
            >
              {{ initials }}
            </el-avatar>
            <i class="el-icon-arrow-down el-icon--right"></i>
          </span>
        </el-popover>
        <div class="separator"></div>
      </div>
    </div>
    <div></div>
    <transition name="slide" mode="out-in" appear>
      <router-view />
    </transition>

    <bly-modal :show.sync="showTos" width="sm:max-w-4xl w-full z-9999" :overflow="false" :disableClose="true">
      <h3 slot="header">Terms of use</h3>
      <div class="h-[calc(100vh-60px)] overflow-y-scroll -mx-4 px-8 py-3">
        <bly-tos />
        <div class="flex py-8 justify-between">
          <button class="bg-black btn px-8 py-1" @click="logout">Logout</button>
          <button class="bg-yellow-500 btn px-8 py-1" @click="acceptTos">Accept</button>
        </div>
      </div>
    </bly-modal>
  </div>
</template>
<script>
import { mapState, mapMutations, mapActions } from "vuex";
import { isMobile, isMobileOnly } from "mobile-device-detect";
import { getCurrentUser, fetchAuthSession, signOut, fetchUserAttributes } from 'aws-amplify/auth';
import { mapGetters as mapGettersHelper } from "vuex";
import AuthenticationService from "@/services/Authentication";
import ColorGenerator from "@/services/ColorGenerator";

export default {
  data() {
    return {
      interval: null,
      userName: "",
      mobileNav: false,
      isMobile,
      isMobileOnly,
      showUserMenu: false,
      showTos: false,
      showNotifications: false,
    };
  },

  computed: {
    ...mapGettersHelper('notifications', ['unreadCount', 'allNotifications']),
    ...mapState({
      showDataFromAllAccounts: (state) => state.userAccount.showDataFromAllAccounts,
      isSysAdmin: (state) => state.userAccount.isSysAdmin,
      userSession: (state) => state.userAccount.userSession,
      userGroups: (state) => state.userAccount.userGroups,
      client: (state) => state.userAccount.client,
      listClients: (state) => state.userAccount.listClients,
      clients: (state) => state.userAccount.clients,
      account: (state) => state.userAccount.account,
      userFeatures: (state) => state.userAccount.userFeatures,
    }),
    initials() {
      if (!this.account.name) return "";
      return this.account.name
        .split(" ")
        .map((n) => n[0])
        .join("");
    },
    accountName() {
      return this.account && this.account.name ? this.account.name : "";
    },

    avatarBackgroundColor() {
      return ColorGenerator.generateNewColor(this.accountName + this.account.code);
    },

    avatarTextColor() {
      return "#fff";
    },

    avaiableClients() {
      const clients = [];
      if (!this.listClients.items) return [];
      this.listClients.items.forEach((client) => {
        const accounts = [];
        client.accounts.items.forEach((account) => {
          const isDefault = account.code === client.code;
          accounts.push({
            client,
            account,
            isDefault,
          });
        });
        accounts.sort((a, b) => a.account.name.localeCompare(b.account.name));
        clients.push({
          client,
          accounts,
        });
      });

      return clients;
    },

    avaiableAccounts() {
      const accounts = [];
      if (!this.listClients.items) return [];
      this.listClients.items.forEach((client) => {
        client.accounts.items.forEach((account) => {
          const isDefault = account.code === client.code;
          accounts.push({
            account,
            client,
            isDefault,
          });
        });
      });

      // Sort accounts by account name
      accounts.sort((a, b) => a.account.name.localeCompare(b.account.name));

      return accounts;
    },
  },

  created() {
    const body = document.querySelector("body");
    body.className = this.isMobileOnly ? "mobile" : "desktop";
    this.interval = window.setInterval(this.checkSession, 60000);
    this.checkUser();
    this.fetchUserClientList();
    this.fetchUserAccountList();
    this.listAppSettings();
    this.$store.dispatch('notifications/subscribe');
  },

  mounted() {
    this.checkTos();
  },

  beforeDestroy() {
    window.clearInterval(this.interval);
    this.$store.dispatch('notifications/unsubscribe');
  },

  methods: {
    ...mapActions({
      fetchUserClientList: "userAccount/fetchClientList",
      fetchUserAccountList: "userAccount/fetchAccountList",
      listAppSettings: "appSettings/listAppSettings",
    }),

    ...mapMutations({
      setCognitoUser: "userAccount/setCognitoUser",
      setPermissions: "userAccount/setPermissions",
      setClient: "userAccount/setClient",
      setClientAccount: "userAccount/setClientAccount",
      setShowDataFromAllAccounts: "userAccount/setShowDataFromAllAccounts",
    }),

    openTermsOfService() {
      this.showTos = true;
      this.showUserMenu = false;
    },
    acceptTos() {
      localStorage.setItem("tosAccepted", new Date());
      this.showTos = false;
    },

    checkTos() {
      const tosAccepted = localStorage.getItem("tosAccepted");
      if (!tosAccepted) {
        this.showTos = true;
        return;
      }

      const tosDate = new Date(tosAccepted);
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      if (tosDate < oneYearAgo) {
        this.showTos = true;
      }
    },

    async logout() {
      this.mobileNav = false;
      await signOut();
      this.$router.push({ path: "/login" });
    },

    profile() {
      this.mobileNav = false;
      this.$router.push({ path: "/profile" });
    },

    async checkUser() {
      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        const attributes = await fetchUserAttributes();

        // Create a compatible user object for the legacy AuthenticationService
        const compatibleUser = {
          ...user,
          attributes: attributes,
          signInUserSession: {
            accessToken: {
              jwtToken: session.tokens?.accessToken?.toString(),
              payload: session.tokens?.accessToken?.payload || {}
            },
            idToken: {
              payload: session.tokens?.idToken?.payload || {}
            }
          }
        };

        AuthenticationService.setUser(compatibleUser);
        console.log("user => ", compatibleUser.signInUserSession.accessToken.payload);
        this.setCognitoUser(compatibleUser);
        this.setPermissions(AuthenticationService.userPermissions);
        this.userName = AuthenticationService.userEmail;
      } catch (error) {
        console.error('Error in checkUser:', error);
      }
    },

    async checkSession() {
      await AuthenticationService.checkSession();
    },

    markRead(index) {
      this.$store.commit('notifications/markAsRead', index);
    },

    markAllAsRead() {
      this.$store.commit('notifications/markAllAsRead');
    },
    statusDotClass(status) {
      if (!status) return 'bg-slate-600';
      const s = status.toUpperCase();
      if (s === 'ERROR' || s === 'CRITICAL') return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]';
      if (s === 'WARNING') return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]';
      if (s === 'OK' || s === 'HEALTHY') return 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]';
      return 'bg-slate-600';
    },
    statusDotClass(status) {
      if (!status) return 'bg-slate-600';
      const s = status.toUpperCase();
      if (s === 'ERROR' || s === 'CRITICAL') return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]';
      if (s === 'WARNING') return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]';
      if (s === 'OK' || s === 'HEALTHY') return 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]';
      return 'bg-slate-600';
    },

    statusClass(status) {
      if (!status) return 'bg-slate-800 text-slate-500';
      const s = status.toUpperCase();
      if (s === 'ERROR' || s === 'CRITICAL') return 'bg-red-500/20 text-red-500';
      if (s === 'WARNING') return 'bg-amber-500/20 text-amber-500';
      if (s === 'OK' || s === 'HEALTHY') return 'bg-emerald-500/20 text-emerald-500';
      return 'bg-slate-800 text-slate-400';
    },

    statusBadgeClass(status) {
      if (!status) return 'bg-slate-700/60 text-slate-400';
      const s = status.toUpperCase();
      if (s === 'ERROR' || s === 'CRITICAL') return 'bg-red-500 text-red-50';
      if (s === 'WARNING') return 'bg-amber-500 text-amber-50';
      if (s === 'OK' || s === 'HEALTHY') return 'bg-emerald-500 text-emerald-50';
      return 'bg-slate-700/60 text-slate-400';
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const d = new Date(timestamp);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
<style>
@reference "../index.css";

.el-loading-spinner svg {
  margin: 0 auto;
}

.highlight-event {
  background: rgba(255, 159, 49, 0.2) !important;
}

.agenda-filter-popup .group-label {
  margin-top: 10px;
  display: inline-block;
}

.output-status-null {
  display: none;
}

.output-DELETED {
  text-decoration: line-through;
}

.event-group {
  position: relative;
}

.collapse {
  position: absolute;
  left: -24px;
}

.h-5 {
  height: 24px;
}

.w-5 {
  width: 24px;
}

.select-multible {
  position: fixed;
  z-index: 11;
  float: right;
  right: 380px;
  margin-top: 2px;
}

.sticky-date {
  position: fixed;
  z-index: 11;
  background: #000;
  width: 138px;
  /* top: 0; */
  margin-top: -5px;
  padding: 11px;
  color: #cfdfff;
  background: #060809;
  padding: 9px 17px 9px 37px;
  display: inline-block;
  opacity: 1;
  margin-left: -19px;
  font-size: 16px;
}

#agenda-view {
  padding: 20px;
  margin-top: 36px;
}
#agenda-view .wrapper {
  height: calc(100vh - 146px);
}
#agenda-view i.bi.bly-filter.icon-btn.active {
  color: #cfdfff;
}
#agenda-view i.bi.bly-filter.icon-btn.active:after {
  content: " ";
  display: block;
  width: 9px;
  height: 9px;
  background: red;
  border-radius: 5px;
  position: absolute;
  top: 5px;
  right: 4px;
  border: 2px solid #343c49;
}
#agenda-view .tx-report-booking-details {
  border: 1px solid #ddd;
  padding: 16px;
  border-radius: 4px;
}
#agenda-view .tx-report-booking-details .content {
  color: #000;
}
#agenda-view .tx-report-booking-details .label {
  font-weight: 600;
  font-size: 11px;
  margin-bottom: 2px;
  text-transform: uppercase;
  opacity: 0.6;
  margin-top: 16px;
}
#agenda-view .agenda-header-columns {
  display: grid;
  outline: 2px solid #13191d;
  grid-gap: 8px;
}
#agenda-view .agenda-header {
  color: #cfdfff;
  display: grid;
  padding: 12px 16px;
  background: #343c49;
  font-size: 12px;
  font-weight: bold;
  /* border-radius: 3px; */
  position: fixed;
  top: 97px;
  left: 20px;
  right: 379px;
  z-index: 99;
  border-top: 5px solid #13191d;
}
#agenda-view .agenda-header .agenda-header-columns {
  display: grid;
}
#agenda-view .agenda-header .agenda-header-settings {
  float: right;
  position: absolute;
  right: 0;
}
#agenda-view .agenda-header .agenda-header-filter {
  position: absolute;
  right: 40px;
}

#agenda-view .agenda-day-group {
  margin-bottom: 16px;
  font-weight: 600;
}
#agenda-view .agenda-day-group-name {
  margin-bottom: 4px;
  display: grid;
  grid-template-columns: 80px 70px 70px 100px 100px 130px 240px auto;
  opacity: 0.7;
  font-size: 11px;
  align-items: center;
}
#agenda-view .agenda-day-group-name div {
  color: #cdd6ee;
  padding-left: 16px;
  padding-top: 4px;
  background: #000;
}
#agenda-view .agenda-day-group-name-name {
  grid-column: span 12;
  background: transparent !important;
}
#agenda-view .agenda-day-group-name-name span {
  color: #cfdfff;
  background: #000;
  padding: 4px 16px;
  display: inline-block;
  opacity: 1;
  margin-left: -16px;
  font-size: 16px;
}
#agenda-view .agenda-day-group .agenda-day-event {
  padding: 4px 16px;
  background: #232b33;
  border-radius: 3px;
  margin-bottom: 6px;
  align-items: start;
  color: #cfdfff;
  position: relative;
}
#agenda-view .agenda-day-group .agenda-day-event .agenda-label {
  display: none;
}
#agenda-view .agenda-day-group .agenda-day-event-actions {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #1e242c;
  height: 40px;
  background: linear-gradient(90deg, rgba(30, 36, 44, 0) 0%, rgba(30, 36, 44, 0.91) 40%);
  display: none;
}
#agenda-view .agenda-day-group .agenda-day-event:hover,
#agenda-view .agenda-day-group .agenda-day-event.active {
  outline: 2px solid #ff9f31;
  color: #cfdfff;
  padding: 4px 16px;
}
#agenda-view .agenda-day-group .agenda-day-event:hover .agenda-day-event-actions,
#agenda-view .agenda-day-group .agenda-day-event.active .agenda-day-event-actions {
  display: block;
}
#agenda-view .agenda-day-group .agenda-day-event-details {
  grid-template-columns: 80px 70px 70px 100px 100px 130px 240px auto;
  display: grid;
  grid-gap: 8px;
}
#agenda-view .agenda-day-group .agenda-day-event-name {
  color: #fff;
  background: rgba(6, 8, 9, 0.18);
  padding: 4px 16px;
  margin: -1px -12px 4px -12px;
  border-radius: 3px;
}
#agenda-view .agenda-day-group .agenda-day-event-name .agenda-day-event-name.LIVE:after {
  position: absolute;
  display: block;
  content: " ";
  left: -6px;
  border: 3px solid #fff;
  right: -6px;
  top: -6px;
  bottom: -6px;
}
#agenda-view .agenda-day-group .agenda-day-event-name span.fc-event-account {
  background: #13191d;
  line-height: 30px;
  margin: -6px -16px -4px -16px;
  padding: 0 10px;
  float: right;
  text-transform: uppercase;
  height: 30px;
  font-size: 12px;
}
#agenda-view .agenda-day-group .agenda-day-event-name span.fc-event-account-client {
  color: #cdd6ee;
  margin: 0 -10px 0px 4px;
  display: inline-block;
  padding: 0px 0px 0px;
}
#agenda-view .agenda-day-group .agenda-day-event-name.LIVE:after {
  content: "";
  position: absolute;
  left: -6px;
  top: -6px;
  bottom: -6px;
  right: -6px;
  border: 3px solid #fff;
}
#agenda-view .agenda-day-group .agenda-day-event-name-description {
  display: inline-block;
  font-size: 13px;
  font-weight: normal;
}
#agenda-view .agenda-day-group .agenda-day-event-startTime small {
  opacity: 0.7;
  font-size: 11px;
  display: block;
}
#agenda-view .agenda-day-group .agenda-day-event-endTime small {
  opacity: 0.7;
  font-size: 11px;
  display: block;
}
#agenda-view .agenda-day-group .agenda-day-event-kickOff small {
  opacity: 0.7;
  font-size: 11px;
  display: block;
}
#agenda-view .agenda-day-group .agenda-day-event-Audio {
  white-space: pre-line;
  margin-top: -16px;
  font-size: 13px;
}
#agenda-view .agenda-day-group .agenda-day-event-Input {
  word-break: break-all;
}
#agenda-view .agenda-day-group .agenda-day-event-Outputs {
  word-break: break-all;
}
#agenda-view .agenda-day-group .agenda-day-event-Outputs .other div {
  margin-bottom: 2px;
}
#agenda-view .agenda-day-group .agenda-day-event-Outputs .show-ma {
  font-size: 80%;
  opacity: 0.8;
  margin-bottom: 4px !important;
}
#agenda-view .agenda-day-group .agenda-day-event-Outputs .show-ma.null {
  display: none;
}
@media screen and (max-width: 1200px) {
  #agenda-view .agenda-day-group .agenda-day-event {
    display: grid;
    padding: 4px 16px;
    grid-template-columns: 70px 70px 70px 100px 120px 150px 100px 150px 150px;
    grid-template-rows: 1fr 1fr;
  }
  #agenda-view .agenda-day-group .agenda-day-event-name {
    grid-column: span 9;
  }
}

.mobile #agenda-view {
  padding: 20px;
  margin-top: 10px;
}
.mobile #agenda-view .wrapper {
  height: calc(100vh - 146px);
  width: 100%;
}
.mobile #agenda-view .agenda-header {
  right: 20px;
}
.mobile #agenda-view .agenda-day-group .agenda-day-event {
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
}
.mobile #agenda-view .agenda-day-group .agenda-day-event-name {
  grid-column: span 9;
}
.mobile #agenda-view .agenda-day-group .agenda-day-event-details {
  grid-template-columns: 1fr;
}
.mobile #agenda-view .agenda-day-group .agenda-day-event-details > div {
  display: grid;
  grid-template-columns: 120px 1fr;
}
.mobile #agenda-view .agenda-day-group .agenda-header-columns {
  grid-template-columns: 1fr;
}
.mobile #agenda-view .agenda-day-group .agenda-label {
  display: inline-block;
  min-width: 120px;
}
.mobile #agenda-view .agenda-day-group-name {
  grid-template-columns: 1fr auto;
}
.mobile #agenda-view .agenda-header-columns {
  display: none;
  grid-template-columns: 1fr;
}

.theme-mini #agenda-view .agenda-day-group {
  font-weight: 400;
}
.theme-mini #agenda-view .commentary-language {
  color: #fff;
  background: #232b33;
  height: 20px;
  position: relative;
  top: 0px;
  padding-top: 7px;
  padding-left: 10px;
  left: -10px;
  padding-right: 11px;
  padding-bottom: 10px;
}
.theme-mini #agenda-view .agenda-day-event-name {
  background: transparent !important;
}
.theme-mini #agenda-view .agenda-day-event-name .agenda-day-event-name-description {
  color: #66718d !important;
}
.theme-mini #agenda-view .agenda-day-event-name span.fc-event-account {
  background: #13191d;
  line-height: 30px;
  margin: 0;
  padding: 0 10px;
  float: right;
  text-transform: uppercase;
  height: 30px;
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 0 0 0 6px;
}
.theme-mini #agenda-view .agenda-day-event-name:before {
  display: block;
  position: absolute;
  content: " ";
  background: red;
  top: 4px;
  bottom: 4px;
  width: 5px;
  left: 4px;
}
.theme-mini #agenda-view .agenda-day-event-details {
  padding-left: 4px;
}
.theme-mini .event-preview-drawer #el-drawer__title .client-name {
  font-weight: 400;
}

span.moschecked {
  background: #00ff80;
  font-weight: bold;
  color: #fff;
  width: 20px;
  height: 20px;
  display: inline-block;
  text-align: center;
  border-radius: 4px;
  padding-top: 4px;
  font-size: 11px;
  margin-right: 4px;
}

.group-events {
  margin-left: 20px;
}
.group-events.collapsed {
  height: 0;
  overflow: hidden;
}

.remap-tag {
  @apply bg-yellow-500;
  color: #ffffff;
  padding: 0 6px;
  border-radius: 3px;
  position: relative;
  top: -2px;
  font-size: 11px;
  height: 18px;
  display: inline-block;
  margin-left: 6px;
  line-height: 19px;
}
</style>
